import { useState, useEffect, createContext, useContext, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { PageSkeleton } from "@/components/Skeletons";
import { ApplyProvider, useApplyContext } from "@/lib/smartApply";
import QuickOTPModal from "@/components/QuickOTPModal";

import { WalletProvider } from "@/components/TopNav";

// Layout
import AppShell from "@/components/AppShell";
import DesktopLayout from "@/components/DesktopLayout";

// Core pages (loaded eagerly — needed for initial navigation)
import AuthPage from "@/pages/AuthPage";
import FeedPage from "@/pages/FeedPage";
import CollegesPage from "@/pages/CollegesPage";
import ExplorePage from "@/pages/ExplorePage";
import FloatingAI from "@/components/FloatingAI";
import IntentPopup from "@/components/IntentPopup";
import ProfileCompletionBanner from "@/components/ProfileCompletionBanner";
import { IntentProvider } from "@/components/IntentSheet";
import { ActionStatesProvider } from "@/hooks/useActionStates";
import WelcomeScreen from "@/components/WelcomeScreen";
import GuestSignupModal from "@/components/GuestSignupModal";
import FeatureTour from "@/components/FeatureTour";
import { FeatureGateProvider } from "@/hooks/useFeatureGate";
import { GuestProvider, useGuest } from "@/contexts/GuestContext";
import InstallBanner from "@/components/InstallBanner";
import LegalGate from "@/components/legal/LegalGate";

// Lazy-loaded pages (loaded on demand — reduces initial bundle by ~60%)
// Prefetch map for bottom nav hover prefetching
const _prefetchMap = {};
const lazyWithPrefetch = (key, factory) => {
  _prefetchMap[key] = factory;
  return lazy(factory);
};
export const prefetchPage = (key) => {
  if (_prefetchMap[key]) { _prefetchMap[key](); }
};

const MarketplacePage = lazy(() => import("@/pages/MarketplacePage"));
const ChatPage = lazyWithPrefetch('chat', () => import("@/pages/ChatPage"));
const ProfilePage = lazyWithPrefetch('profile', () => import("@/pages/ProfilePage"));
const CreatePostPage = lazy(() => import("@/pages/CreatePostPage"));
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const UniversityProfilePage = lazy(() => import("@/pages/UniversityProfilePage"));
const BucketList = lazy(() => import("@/pages/BucketList"));
const Applications = lazy(() => import("@/pages/Applications"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const InstitutionDashboard = lazy(() => import("@/pages/InstitutionDashboard"));
const VisaServicesPage = lazy(() => import("@/pages/VisaServicesPage"));
const VisaApplicationPage = lazy(() => import("@/pages/VisaApplicationPage"));
const VisaApplyFormPage = lazy(() => import("@/pages/VisaApplyFormPage"));
const VisaProgramDetailPage = lazy(() => import("@/pages/VisaProgramDetailPage"));
const InsurancePage = lazy(() => import("@/pages/InsurancePage"));
const InsuranceDetailPage = lazy(() => import("@/pages/InsuranceDetailPage"));
const RentalsPage = lazy(() => import("@/pages/RentalsPage"));
const RentalDetailPage = lazy(() => import("@/pages/RentalDetailPage"));
const PostRentalPage = lazy(() => import("@/pages/PostRentalPage"));
const VisaTrackingPage = lazy(() => import("@/pages/VisaTrackingPage"));
const AgentDashboard = lazy(() => import("@/pages/AgentDashboard"));
const ClaimAgentPage = lazy(() => import("@/pages/ClaimAgentPage"));
const MagicLoginPage = lazy(() => import("@/pages/MagicLoginPage"));
const AgentPublicProfile = lazy(() => import("@/pages/AgentPublicProfile"));
// UserPublicProfile removed — unified into ProfilePage
const AgentsDirectoryPage = lazy(() => import("@/pages/AgentsDirectoryPage"));
const StudySetupPage = lazy(() => import("@/pages/StudySetupPage"));
const AdmissionQuickApplyPage = lazy(() => import("@/pages/AdmissionQuickApplyPage"));
const VisaSetupPage = lazy(() => import("@/pages/VisaSetupPage"));
const ApplicationFlowPage = lazy(() => import("@/pages/ApplicationFlowPage"));
const MyApplicationsPage = lazy(() => import("@/pages/MyApplicationsPage"));
const MessagingPage = lazy(() => import("@/pages/MessagingPage"));
const StudentDashboard = lazy(() => import("@/pages/StudentDashboard"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const VisaFlowPage = lazy(() => import("@/pages/VisaFlowPage"));
const QuickProfilePage = lazy(() => import("@/pages/QuickProfilePage"));
const StoryProfilePage = lazy(() => import("@/pages/StoryProfilePage"));
const OfferSwipePage = lazy(() => import("@/pages/OfferSwipePage"));
const FileReviewPage = lazy(() => import("@/pages/FileReviewPage"));
const VisaReviewPage = lazy(() => import("@/pages/VisaReviewPage"));
const VisaQuickApplyPage = lazy(() => import("@/pages/VisaQuickApplyPage"));
const QuickApplyOrderSummary = lazy(() => import("@/pages/QuickApplyOrderSummary"));
const QuickApplyProcessing = lazy(() => import("@/pages/QuickApplyProcessing"));
const DocumentVaultPage = lazy(() => import("@/pages/DocumentVaultPage"));
const VisaPrepPage = lazy(() => import("@/pages/VisaPrepPage"));
const RplAssessmentPage = lazy(() => import("@/pages/RplAssessmentPage"));
const RplCompletionPage = lazy(() => import("@/pages/RplCompletionPage"));
const SkillAssessmentPage = lazy(() => import("@/pages/SkillAssessmentPage"));
const SkillCompletionPage = lazy(() => import("@/pages/SkillCompletionPage"));
const UniversalCompletionPage = lazy(() => import("@/pages/UniversalCompletionPage"));
const StudentApplicationDetailPage = lazy(() => import("@/pages/StudentApplicationDetailPage"));
const BuyCreditsPage = lazy(() => import("@/pages/BuyCreditsPage"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const CreditSuccessPage = lazy(() => import("@/pages/CreditSuccessPage"));
const CreditCancelPage = lazy(() => import("@/pages/CreditCancelPage"));
const LegalDocPage = lazy(() => import("@/pages/LegalDocPage"));
const VisaExpressPassportPage = lazy(() => import("@/pages/VisaExpressPassportPage"));
const VisaExpressCheckoutPage = lazy(() => import("@/pages/VisaExpressCheckoutPage"));
const ExpressPassportPage = lazy(() => import("@/pages/ExpressPassportPage"));
const ExpressCheckoutPage = lazy(() => import("@/pages/ExpressCheckoutPage"));
const AdmissionExpressInfoPage = lazy(() => import("@/pages/AdmissionExpressInfoPage"));
const AdmissionExpressPassportPage = lazy(() => import("@/pages/AdmissionExpressPassportPage"));
const SkillsExpressInfoPage = lazy(() => import("@/pages/SkillsExpressInfoPage"));
const SkillsExpressCheckoutPage = lazy(() => import("@/pages/SkillsExpressCheckoutPage"));
const RplExpressInfoPage = lazy(() => import("@/pages/RplExpressInfoPage"));
const RplExpressCheckoutPage = lazy(() => import("@/pages/RplExpressCheckoutPage"));
const TempVisaExpressInfoPage = lazy(() => import("@/pages/TempVisaExpressInfoPage"));
const TempVisaExpressCheckoutPage = lazy(() => import("@/pages/TempVisaExpressCheckoutPage"));
const PrVisaExpressInfoPage = lazy(() => import("@/pages/PrVisaExpressInfoPage"));
const PrVisaExpressCheckoutPage = lazy(() => import("@/pages/PrVisaExpressCheckoutPage"));
const InsuranceExpressInfoPage = lazy(() => import("@/pages/InsuranceExpressInfoPage"));
const InsuranceExpressCheckoutPage = lazy(() => import("@/pages/InsuranceExpressCheckoutPage"));
const ExpressSuccessPage = lazy(() => import("@/pages/ExpressSuccessPage"));
const PrivacySettingsPage = lazy(() => import("@/pages/PrivacySettingsPage"));
const InstitutionClaimPage = lazy(() => import("@/pages/InstitutionClaimPage"));
const SetPasswordPage = lazy(() => import("@/pages/SetPasswordPage"));
const JobDetailPage = lazy(() => import("@/pages/JobDetailPage"));
const RecruiterSignupPage = lazy(() => import("@/pages/RecruiterSignupPage"));
const RecruiterDashboard = lazy(() => import("@/pages/RecruiterDashboard"));
const AgentRegisterPage = lazy(() => import("@/pages/AgentRegisterPage"));
const InstitutionRegisterPage = lazy(() => import("@/pages/InstitutionRegisterPage"));
const ListingsPage = lazy(() => import("@/pages/ListingsPage"));
const AdCentrePage = lazy(() => import("@/pages/AdCentrePage"));
const LandingPage = lazy(() => import("@/preview-landing/LandingPage"));
const OldLandingPage = lazy(() => import("@/pages/LandingPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));

// SEO Landing Pages
const StudyAbroadPage = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.StudyAbroadPage })));
const CountryLandingPage = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.CountryLandingPage })));
const FieldLandingPage = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.FieldLandingPage })));
const StudyLandingPage = lazy(() => import("@/pages/StudyLandingPage"));
const AIAssistantPage = lazyWithPrefetch('ai', () => import("@/pages/AIAssistantPage"));

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Sensible global axios timeout — without this, slow backend requests can
// hang for the browser's full default (~120s), eating HTTP/2 stream slots
// and getting silently killed as ERR_ABORTED on subsequent re-renders.
// 25s leaves room for slow AI endpoints (blog generate ~70s already uses
// per-call overrides) while bounding the stall window on broken endpoints.
axios.defaults.timeout = 25000;

// Axios interceptor — attach JWT from localStorage to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data.user);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    setUser(response.data.user);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem("token");
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, loginWithGoogle, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// Auth Callback
function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
          setUser(response.data.user);
          toast.success(`Welcome, ${response.data.user.name}!`);
          const role = response.data.user.role;
          const userId = response.data.user.user_id;
          if (role === 'admin') navigate('/admin', { replace: true });
          else if (role === 'institution') navigate(`/university/${userData.university_id}`, { replace: true });
          else if (role === 'agent') navigate(`/agents/${userId}`, { replace: true });
          else if (role === 'recruiter') navigate('/recruiter', { replace: true });
          else navigate('/explore', { replace: true });
        } catch (error) {
          toast.error("Authentication failed");
          navigate('/auth', { replace: true });
        }
      } else {
        navigate('/auth', { replace: true });
      }
    };
    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#f5f6f8] flex items-center justify-center">
      <div className="w-10 h-10 border-3 border-[#2F80ED] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Protected Route
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f6f8] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#2F80ED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'institution') return <Navigate to="/institution" replace />;
    if (user.role === 'recruiter') return <Navigate to="/recruiter" replace />;
    return <Navigate to="/explore" replace />;
  }

  return children;
}

// Public shell - shows AppShell without requiring login
function PublicAppRoute({ children }) {
  return <AppShell>{children}</AppShell>;
}

// Feature flag gated route - redirects to /colleges if feature is disabled
function FlagRoute({ flagKey, children }) {
  const { isEnabled, loaded } = usePlatformFlags();
  if (!loaded) return null; // Wait for flags to load
  if (!isEnabled(flagKey)) return <Navigate to="/explore" replace />;
  return children;
}

// Protected shell - requires login
function ProtectedAppRoute({ children, allowedRoles }) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

// Router
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [showIntent, setShowIntent] = useState(false);
  const [showGateOTP, setShowGateOTP] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const applyCtx = useApplyContext();

  // Gate: Guest users can browse freely for 3 minutes
  // After login, show Welcome Screen → then IntentPopup after timer
  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Guest mode — no gate OTP, let them browse
      setShowGateOTP(false);
      return;
    }
    // Logged in user flow
    setShowGateOTP(false);

    // 🔑 Server-side flag wins: if the user has EVER dismissed the welcome
    // or intent popup on any device, the user record carries that flag and
    // we never show it again. This is the source of truth — localStorage is
    // only a fallback for guests / legacy flows.
    const serverSeenWelcome = !!user.has_seen_welcome;
    const serverSeenIntent = !!user.has_seen_intent;
    // Keep localStorage in sync so later session reads are instant
    if (serverSeenWelcome) {
      localStorage.setItem('unexa_welcome_choice', 'server');
      localStorage.setItem('unexa_tour_complete', 'true');
    }
    if (serverSeenIntent) {
      localStorage.setItem('unexa_intent_shown', 'true');
    }

    const welcomeChoice = localStorage.getItem('unexa_welcome_choice');
    const intentShown = localStorage.getItem('unexa_intent_shown') || serverSeenIntent;

    if (!welcomeChoice && !serverSeenWelcome && !intentShown && user.role === 'student') {
      // First-time student: check if has application
      axios.get(`${API}/workflow/profile-status`)
        .then(res => {
          if (res.data?.has_application) {
            localStorage.setItem('unexa_intent_shown', 'true');
            localStorage.setItem('unexa_welcome_choice', 'returning');
            // Persist server-side too so this survives any browser clear
            axios.post(`${API}/users/mark-seen`, { welcome: true, intent: true }).catch(() => {});
          } else {
            setShowWelcome(true);
          }
        })
        .catch(() => {
          // 🐛 FIX: Don't force welcome screen on transient network errors.
          // If profile-status fails we simply skip the popup for this session
          // — user can see it next time if the API recovers.
          // Do NOT setShowWelcome(true) here — that caused the tour to
          // re-appear on every failed API call (cold starts, flaky network).
        });
    } else if (!intentShown && welcomeChoice === 'explore') {
      // User chose "explore" — check if 2-min timer expired
      const exploreStart = parseInt(localStorage.getItem('unexa_explore_start') || '0');
      const elapsed = Date.now() - exploreStart;
      if (elapsed > 120000) {
        setShowIntent(true);
      } else {
        const remaining = 120000 - elapsed;
        const timer = setTimeout(() => setShowIntent(true), remaining);
        return () => clearTimeout(timer);
      }
    } else if (!intentShown && user.role !== 'student') {
      localStorage.setItem('unexa_intent_shown', 'true');
      localStorage.setItem('unexa_welcome_choice', 'skip');
    }
  }, [user, loading, location.pathname]);

  const handleWelcomeChoice = (choice) => {
    setShowWelcome(false);
    // Persist to server so it survives browser clears / new devices
    axios.post(`${API}/users/mark-seen`, { welcome: true }).catch(() => {});
    // After welcome screen, show feature tour if not completed
    const tourComplete = localStorage.getItem('unexa_tour_complete');
    if (!tourComplete) {
      setShowTour(true);
    }
  };

  const handleTourComplete = () => {
    setShowTour(false);
    // Persist to server so the tour never shows again on any device
    axios.post(`${API}/users/mark-seen`, { welcome: true }).catch(() => {});
  };

  // Also allow smart-apply to trigger popup
  const isPopupVisible = (showIntent || applyCtx?.showPopup) && !showGateOTP;

  const handlePopupClose = () => {
    setShowIntent(false);
    localStorage.setItem('unexa_intent_shown', 'true');
    // Persist server-side so it survives browser clears and cross-device
    axios.post(`${API}/users/mark-seen`, { intent: true }).catch(() => {});
    if (applyCtx) applyCtx.closePopup();
  };

  const handleGateOTPSuccess = (userData, token) => {
    setShowGateOTP(false);
    // Redirect non-student roles to their dashboards
    if (userData?.role === 'admin') navigate('/admin', { replace: true });
    else if (userData?.role === 'recruiter') navigate('/recruiter', { replace: true });
    else if (userData?.role === 'agent') navigate(`/agents/${userData.user_id}`, { replace: true });
    else if (userData?.role === 'institution') navigate(`/university/${userData.university_id}`, { replace: true });
    // Students stay — useEffect will check WelcomeScreen/IntentPopup
  };

  const handleSmartApplyOTPSuccess = (userData, token) => {
    applyCtx.closeOTP();
    const pendingRoute = localStorage.getItem('unexa_pending_route');
    if (pendingRoute) {
      localStorage.removeItem('unexa_pending_route');
      navigate(pendingRoute);
    }
  };

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      {/* Guest Signup Modal — shown after 3 min or on action click */}
      <GuestSignupModal />
      {/* Welcome Screen — first-time student onboarding */}
      {showWelcome && (
        <WelcomeScreen onChoice={handleWelcomeChoice} />
      )}
      {/* Feature Tour — step-by-step tooltip tour */}
      {showTour && (
        <FeatureTour onComplete={handleTourComplete} />
      )}
      {/* Gate OTP — unskippable, blurred bg, must sign up to enter */}
      {showGateOTP && (
        <QuickOTPModal
          onSuccess={handleGateOTPSuccess}
          onClose={() => {}} /* no-op: can't dismiss */
          dismissable={false}
        />
      )}
      {/* IntentPopup — shown after signup or on smart-apply trigger */}
      {isPopupVisible && (
        <IntentPopup
          onClose={handlePopupClose}
          preSelected={applyCtx?.preSelected || null}
        />
      )}
      {/* Smart-apply OTP (triggered from within flows) */}
      {applyCtx?.showOTP && !showGateOTP && (
        <QuickOTPModal
          onSuccess={handleSmartApplyOTPSuccess}
          onClose={() => applyCtx.closeOTP()}
        />
      )}
      {!location.pathname.startsWith('/complete/') && !location.pathname.startsWith('/apply/success/') && <ProfileCompletionBanner />}
      <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* Public */}
        {/* Homepage — Explore is now the default landing for all users.
            Logged-in admins/recruiters/agents/institutions are routed
            to their role-specific dashboards by ExplorePage's inner
            redirect logic; everyone else (guests + students) sees
            Explore. The old marketing LandingPage component file is
            retained at /preview-landing for reference but is no longer
            routed. Mar 1, 2026. */}
        <Route path="/" element={<PublicAppRoute><DesktopLayout><ExplorePage /></DesktopLayout></PublicAppRoute>} />
        {/* Legacy /explore → / so existing inbound links and bookmarks
            continue working. SPA Navigate replaces history so back-button
            doesn't bounce. A true 301 happens at the Cloudflare/ingress
            layer for SEO preservation. */}
        <Route path="/explore" element={<Navigate to="/" replace />} />
        {/* Old marketing landing page — preserved for preview only */}
        <Route path="/old-landing" element={<PublicAppRoute><LandingPage /></PublicAppRoute>} />
        <Route path="/preview-landing" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/join" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Public browsable pages - no login needed */}
        {/* Flow is the new name for Feed — /feed redirects to /flow to keep old links alive */}
        <Route path="/flow" element={<PublicAppRoute><FeedPage /></PublicAppRoute>} />
        <Route path="/feed" element={<Navigate to="/flow" replace />} />
        {/* Old standalone pages now redirect to Explore — routes defined above */}
        <Route path="/marketplace" element={<PublicAppRoute><MarketplacePage /></PublicAppRoute>} />
        <Route path="/courses/:courseId" element={<PublicAppRoute><CourseDetail /></PublicAppRoute>} />
        <Route path="/university/:universityId" element={<PublicAppRoute><UniversityProfilePage /></PublicAppRoute>} />
        <Route path="/agents" element={<Navigate to="/explore?service=agents" replace />} />
        <Route path="/agents/:userId" element={<PublicAppRoute><AgentPublicProfile /></PublicAppRoute>} />

        {/* Colleges, Visa, Insurance, Rentals -> redirect to Explore */}
        <Route path="/colleges" element={<Navigate to="/explore?service=colleges" replace />} />
        <Route path="/visa" element={<Navigate to="/explore?service=visa" replace />} />
        <Route path="/insurance" element={<Navigate to="/explore?service=insurance" replace />} />
        <Route path="/rentals" element={<Navigate to="/explore?service=rentals" replace />} />

        {/* SEO Landing Pages - public, crawlable */}
        <Route path="/terms" element={<LegalDocPage documentType="terms" />} />
        <Route path="/privacy" element={<LegalDocPage documentType="privacy" />} />
        <Route path="/study" element={<PublicAppRoute><StudyAbroadPage /></PublicAppRoute>} />
        <Route path="/study/:country" element={<PublicAppRoute><CountryLandingPage /></PublicAppRoute>} />
        <Route path="/study/:country/:field" element={<PublicAppRoute><FieldLandingPage /></PublicAppRoute>} />

        {/* Programmatic SEO — field+city course pages */}
        <Route path="/study-courses/:field" element={<PublicAppRoute><StudyLandingPage /></PublicAppRoute>} />
        <Route path="/study-courses/:field/:city" element={<PublicAppRoute><StudyLandingPage /></PublicAppRoute>} />

        {/* Blog - Public SEO Pages */}
        <Route path="/blog" element={<PublicAppRoute><DesktopLayout><BlogPage /></DesktopLayout></PublicAppRoute>} />
        <Route path="/blog/:slug" element={<PublicAppRoute><BlogPostPage /></PublicAppRoute>} />
        {/* /news/:slug renders the same BlogPostPage — separate URL space for news content for clearer URLs + schema.org separation */}
        <Route path="/news/:slug" element={<PublicAppRoute><BlogPostPage /></PublicAppRoute>} />

        {/* AI Assistant — accessible to guests with signup prompt */}
        <Route path="/ai" element={<PublicAppRoute><AIAssistantPage /></PublicAppRoute>} />

        {/* Institution Claim & Set Password & Agent Register (public, no auth needed) */}
        <Route path="/claim/invite/:token" element={<InstitutionClaimPage />} />
        <Route path="/set-password/:token" element={<SetPasswordPage />} />
        <Route path="/register/agent" element={<AgentRegisterPage />} />
        <Route path="/register/institution" element={<InstitutionRegisterPage />} />

        <Route path="/user/:userId" element={<ProtectedAppRoute><DesktopLayout><ProfilePage /></DesktopLayout></ProtectedAppRoute>} />
        <Route path="/privacy-settings" element={<ProtectedAppRoute><PrivacySettingsPage /></ProtectedAppRoute>} />
        {/* Old listing pages redirect to Explore (redirects defined above) */}
        {/* Detail pages still work */}
        <Route path="/visa/program/:programId" element={<PublicAppRoute><VisaProgramDetailPage /></PublicAppRoute>} />
        <Route path="/visa/track/:appId" element={<ProtectedAppRoute><VisaTrackingPage /></ProtectedAppRoute>} />
        <Route path="/insurance/:productId" element={<PublicAppRoute><InsuranceDetailPage /></PublicAppRoute>} />
        <Route path="/rentals/post" element={<ProtectedRoute><PostRentalPage /></ProtectedRoute>} />
        <Route path="/rentals/:listingId" element={<PublicAppRoute><RentalDetailPage /></PublicAppRoute>} />

        {/* Community Listings */}
        <Route path="/listings" element={<ProtectedAppRoute><DesktopLayout><ListingsPage /></DesktopLayout></ProtectedAppRoute>} />
        <Route path="/ad-centre" element={<ProtectedRoute><AdCentrePage /></ProtectedRoute>} />

        {/* Workflow - Study & Visa Setup (public start, auth needed for application) */}
        <Route path="/get-started" element={<ProtectedAppRoute><OnboardingPage /></ProtectedAppRoute>} />
        <Route path="/visa-flow" element={<ProtectedAppRoute><VisaFlowPage /></ProtectedAppRoute>} />
        <Route path="/visa-flow/:applicationId" element={<ProtectedAppRoute><VisaFlowPage /></ProtectedAppRoute>} />
        <Route path="/quick-profile/:appId" element={<ProtectedAppRoute><QuickProfilePage /></ProtectedAppRoute>} />
        <Route path="/complete-profile" element={<ProtectedAppRoute><StoryProfilePage /></ProtectedAppRoute>} />
        <Route path="/offers" element={<ProtectedAppRoute><OfferSwipePage /></ProtectedAppRoute>} />
        <Route path="/file-review/:offerId" element={<ProtectedAppRoute><FileReviewPage /></ProtectedAppRoute>} />
        <Route path="/visa-review/:reviewId" element={<ProtectedAppRoute><VisaReviewPage /></ProtectedAppRoute>} />
        <Route path="/apply/study-setup" element={<PublicAppRoute><StudySetupPage /></PublicAppRoute>} />
        <Route path="/admission-apply/:appId" element={<ProtectedAppRoute><AdmissionQuickApplyPage /></ProtectedAppRoute>} />
        <Route path="/apply/visa-setup" element={<PublicAppRoute><VisaSetupPage /></PublicAppRoute>} />
        <Route path="/apply/visa-express/passport" element={<ProtectedAppRoute><VisaExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/visa-express/checkout" element={<ProtectedAppRoute><VisaExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/express/passport" element={<ProtectedAppRoute><ExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/express/checkout" element={<ProtectedAppRoute><ExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/admission-express/info" element={<ProtectedAppRoute><AdmissionExpressInfoPage /></ProtectedAppRoute>} />
        <Route path="/apply/admission-express/passport" element={<ProtectedAppRoute><AdmissionExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/admission-express/checkout" element={<ProtectedAppRoute><ExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/skills-express/info" element={<ProtectedAppRoute><SkillsExpressInfoPage /></ProtectedAppRoute>} />
        <Route path="/apply/skills-express/passport" element={<ProtectedAppRoute><AdmissionExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/skills-express/checkout" element={<ProtectedAppRoute><SkillsExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/rpl-express/info" element={<ProtectedAppRoute><RplExpressInfoPage /></ProtectedAppRoute>} />
        <Route path="/apply/rpl-express/passport" element={<ProtectedAppRoute><AdmissionExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/rpl-express/checkout" element={<ProtectedAppRoute><RplExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/temp-visa-express/info" element={<ProtectedAppRoute><TempVisaExpressInfoPage /></ProtectedAppRoute>} />
        <Route path="/apply/temp-visa-express/passport" element={<ProtectedAppRoute><AdmissionExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/temp-visa-express/checkout" element={<ProtectedAppRoute><TempVisaExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/pr-visa-express/info" element={<ProtectedAppRoute><PrVisaExpressInfoPage /></ProtectedAppRoute>} />
        <Route path="/apply/pr-visa-express/passport" element={<ProtectedAppRoute><AdmissionExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/pr-visa-express/checkout" element={<ProtectedAppRoute><PrVisaExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/insurance-express/info" element={<ProtectedAppRoute><InsuranceExpressInfoPage /></ProtectedAppRoute>} />
        <Route path="/apply/insurance-express/passport" element={<ProtectedAppRoute><AdmissionExpressPassportPage /></ProtectedAppRoute>} />
        <Route path="/apply/insurance-express/checkout" element={<ProtectedAppRoute><InsuranceExpressCheckoutPage /></ProtectedAppRoute>} />
        <Route path="/apply/success/:appId" element={<ProtectedAppRoute><ExpressSuccessPage /></ProtectedAppRoute>} />
        <Route path="/visa-apply/:appId" element={<ProtectedAppRoute><VisaQuickApplyPage /></ProtectedAppRoute>} />
        <Route path="/quick-apply/summary" element={<ProtectedAppRoute><QuickApplyOrderSummary /></ProtectedAppRoute>} />
        <Route path="/quick-apply/processing" element={<ProtectedAppRoute><QuickApplyProcessing /></ProtectedAppRoute>} />
        <Route path="/apply/:appId" element={<ProtectedAppRoute><ApplicationFlowPage /></ProtectedAppRoute>} />
        <Route path="/my-applications" element={<ProtectedAppRoute><MyApplicationsPage /></ProtectedAppRoute>} />
        <Route path="/application/:appId" element={<ProtectedAppRoute><StudentApplicationDetailPage /></ProtectedAppRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['student']}><StudentDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/*" element={<ProtectedRoute allowedRoles={['student']}><StudentDashboard /></ProtectedRoute>} />
        <Route path="/edit-profile" element={<ProtectedAppRoute><Navigate to="/dashboard?tab=mydetails" replace /></ProtectedAppRoute>} />
        <Route path="/messages" element={<ProtectedAppRoute><MessagingPage /></ProtectedAppRoute>} />

        {/* Protected pages - login required */}
        <Route path="/visa/apply-form" element={<ProtectedAppRoute><VisaApplyFormPage /></ProtectedAppRoute>} />
        <Route path="/visa/:visaId" element={<ProtectedAppRoute><VisaApplicationPage /></ProtectedAppRoute>} />
        <Route path="/chat" element={<ProtectedAppRoute><DesktopLayout><ChatPage /></DesktopLayout></ProtectedAppRoute>} />
        <Route path="/profile" element={<ProtectedAppRoute><DesktopLayout><ProfilePage /></DesktopLayout></ProtectedAppRoute>} />
        <Route path="/vault" element={<ProtectedAppRoute><DocumentVaultPage /></ProtectedAppRoute>} />
        <Route path="/visa-prep/:appId" element={<ProtectedAppRoute><VisaPrepPage /></ProtectedAppRoute>} />
        <Route path="/rpl" element={<ProtectedAppRoute><RplAssessmentPage /></ProtectedAppRoute>} />
        <Route path="/rpl-complete/:appId" element={<ProtectedAppRoute><RplCompletionPage /></ProtectedAppRoute>} />
        <Route path="/skill-assessment" element={<ProtectedAppRoute><SkillAssessmentPage /></ProtectedAppRoute>} />
        <Route path="/skill-complete/:appId" element={<ProtectedAppRoute><SkillCompletionPage /></ProtectedAppRoute>} />
        <Route path="/complete/:appId" element={<ProtectedAppRoute><UniversalCompletionPage /></ProtectedAppRoute>} />
        <Route path="/create" element={<ProtectedAppRoute><CreatePostPage /></ProtectedAppRoute>} />
        <Route path="/credits" element={<ProtectedAppRoute><BuyCreditsPage /></ProtectedAppRoute>} />
        <Route path="/credits/success" element={<ProtectedAppRoute><CreditSuccessPage /></ProtectedAppRoute>} />
        <Route path="/credits/cancel" element={<ProtectedAppRoute><CreditCancelPage /></ProtectedAppRoute>} />
        <Route path="/bucket-list" element={<ProtectedAppRoute><BucketList /></ProtectedAppRoute>} />
        <Route path="/applications" element={<ProtectedAppRoute><Applications /></ProtectedAppRoute>} />
        <Route path="/subscriptions" element={<ProtectedAppRoute><SubscriptionPage /></ProtectedAppRoute>} />

        {/* Admin */}
        <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />

        {/* Institution */}
        <Route path="/institution" element={<ProtectedRoute allowedRoles={['institution']}><InstitutionDashboard /></ProtectedRoute>} />
        <Route path="/institution/*" element={<ProtectedRoute allowedRoles={['institution']}><InstitutionDashboard /></ProtectedRoute>} />

        {/* Agent */}
        <Route path="/agent" element={<ProtectedRoute allowedRoles={['agent']}><AgentDashboard /></ProtectedRoute>} />
        <Route path="/agent/*" element={<ProtectedRoute allowedRoles={['agent']}><AgentDashboard /></ProtectedRoute>} />
        <Route path="/claim/agent" element={<ClaimAgentPage />} />
        <Route path="/magic-login" element={<MagicLoginPage />} />

        {/* Recruiter Routes */}
        <Route path="/recruiter" element={<ProtectedRoute allowedRoles={['recruiter']}><RecruiterDashboard /></ProtectedRoute>} />
        <Route path="/recruiter/*" element={<ProtectedRoute allowedRoles={['recruiter']}><RecruiterDashboard /></ProtectedRoute>} />
        <Route path="/recruiter-signup" element={<RecruiterSignupPage />} />
        <Route path="/jobs/:jobId" element={<ProtectedAppRoute><JobDetailPage /></ProtectedAppRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to={user ? "/explore" : "/"} replace />} />
      </Routes>
      </Suspense>
    </>
  );
}

import { PlatformFlagsProvider } from '@/hooks/usePlatformFlags';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppWithGuest() {
  const { user, loading } = useAuth();
  return (
    <GuestProvider user={user} loading={loading}>
      <AppRouter />
    </GuestProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <PlatformFlagsProvider>
        <FeatureGateProvider>
        <WalletProvider>
        <ApplyProvider>
        <IntentProvider>
        <ActionStatesProvider>
        <Toaster 
          position="top-center" 
          richColors 
          theme="light"
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              color: '#1e293b',
            }
          }}
        />
        <AppWithGuest />
        {/* Global legal re-acceptance gate — renders nothing for
            unauthenticated users / when up to date / on public legal
            routes. Lives outside the route tree so it persists across
            navigations. */}
        <LegalGate />
        {/* FloatingAI removed */}
        <InstallBanner />
        </ActionStatesProvider>
        </IntentProvider>
        </ApplyProvider>
        </WalletProvider>
        </FeatureGateProvider>
        </PlatformFlagsProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
export { API, BACKEND_URL };
